import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Mail, RefreshCw, Loader2 } from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';
import { StatusBadge, FrameworkLine, NextStepsBox } from './directoryWidgetCommon';
import FindingCrossLinks from './FindingCrossLinks';

const RecoveryContactsWidget = ({ workspaceId, gwConnected }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const resp = await axios.get('/api/employees/recovery-contacts', {
        params: { workspace_id: workspaceId },
      });
      setData(resp.data);
    } catch (err) {
      console.error('recovery-contacts load failed', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  if (!gwConnected) return null;

  const t = data?.totals || {};
  const pct = t.coverage_pct || 0;
  const target = t.pass_threshold_pct || 95;

  return (
    <Card className="border-slate-200 mb-6" data-testid="recovery-contacts-widget">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
              Account Recovery Contact Coverage
              {data && <StatusBadge status={data.status} />}
            </CardTitle>
            <FrameworkLine>
              SOC 2 CC6.1 · ISO 27001 A.5.17
            </FrameworkLine>
          </div>
          <Button onClick={load} variant="ghost" size="sm" disabled={loading}
                  data-testid="recovery-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loading && !data && (
          <div className="py-6 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Evaluating directory…
          </div>
        )}

        {data && (
          <>
            <p className="text-sm text-slate-700 mb-3" data-testid="recovery-proof">{data.proof}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className={`border rounded-lg p-3 ${pct >= target ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : pct >= 80 ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
                <div className="text-2xl font-semibold leading-none">{pct}%</div>
                <div className="text-xs font-medium mt-1">Coverage · target {target}%</div>
              </div>
              <div className="border rounded-lg p-3 bg-slate-50 border-slate-200 text-slate-700">
                <div className="text-2xl font-semibold leading-none">{t.with_recovery_contact}</div>
                <div className="text-xs font-medium mt-1">With recovery contact</div>
              </div>
              <div className="border rounded-lg p-3 bg-slate-50 border-slate-200 text-slate-700">
                <div className="text-2xl font-semibold leading-none">{t.without_recovery_contact}</div>
                <div className="text-xs font-medium mt-1">Without recovery contact</div>
              </div>
              <div className={`border rounded-lg p-3 ${t.admins_without_recovery > 0 ? 'bg-red-50 border-red-200 text-red-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
                <div className="text-2xl font-semibold leading-none">{t.admins_without_recovery}</div>
                <div className="text-xs font-medium mt-1">Admin(s) without recovery — critical</div>
              </div>
            </div>

            {(data.admins_without_recovery || []).length > 0 && (
              <div className="border rounded-lg border-red-200 bg-red-50/40 mb-3" data-testid="recovery-admins-list">
                <div className="px-3 py-2 border-b border-red-100 text-xs font-medium text-red-900">
                  Admins without recovery contact (critical)
                </div>
                <div className="divide-y divide-red-100 bg-white rounded-b-lg">
                  {data.admins_without_recovery.map((u) => (
                    <div key={u.source_user_id} className="px-3 py-2 text-sm">
                      <div className="font-medium text-slate-900">{u.full_name || u.primary_email}</div>
                      <div className="text-xs text-slate-500">{u.primary_email}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <NextStepsBox text={data.what_would_fix} />

            <FindingCrossLinks
              ruleKey="directory.access.recovery_contacts"
              matches={(data.admins_without_recovery || []).map(u => ({ email: u.primary_email }))}
              workspaceId={workspaceId}
            />

            <div className="mt-3 text-[11px] text-slate-400">
              Evaluated {formatDate(data.evaluated_at)} · {t.active_users} active users
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RecoveryContactsWidget;
